/**
 * Design tokens da identidade Palmer
 *
 * Fonte da verdade: Docs/design/design-system-palmer.md
 * Manual da marca: navy #213668 · verde #70b746 (#51a933 nos SVGs) · azul #316CB4
 *
 * Todos os contrastes citados foram calculados (WCAG 2.1), não estimados.
 */

// ==================== RAMPAS ====================

/**
 * Navy institucional. `#213668` tem luminância 0.0396: é tinta escura, não um
 * azul de ação médio; por isso ancora no passo 800, não no 500.
 */
export const navy = {
  50: '#f6f7f9',
  100: '#ebedf1',
  200: '#d7dbe4',
  300: '#bcc3d2',
  400: '#99a3ba',
  500: '#7582a1',
  600: '#56668c',
  700: '#3c4e7a',
  800: '#213668', // oficial: PRIMÁRIO
  900: '#172649',
} as const;

/**
 * Verde da marca. Os DOIS valores oficiais coexistem: `#70b746` (manual) no 500
 * e `#51a933` (SVGs) no 600.
 *
 * ATENÇÃO: verde-500 com texto branco dá 2.46:1 e reprova WCAG AA até para texto
 * grande. O verde é assinatura geométrica (barra, triângulo, ink bar), nunca
 * superfície de texto. Texto "verde" sobre branco usa green-800 ou `success`.
 */
export const green = {
  50: '#f4f9f0',
  100: '#e2f1da',
  200: '#c9e4b9',
  300: '#acd594',
  400: '#8dc56b',
  500: '#70b746', // oficial (manual): ACENTO DA MARCA
  600: '#51a933', // oficial (SVGs)
  700: '#418729',
  800: '#326920',
  900: '#254e17',
} as const;

/** Azul da conexão. `#316cb4` dá 5.34:1 sobre branco e serve como link AA sem ajuste. */
export const blue = {
  50: '#f3f6fb',
  100: '#e2eaf5',
  200: '#c1d3e9',
  300: '#9cb8db',
  400: '#739bcc',
  500: '#5082bf',
  600: '#316cb4', // oficial: LINK / INFO
  700: '#275690',
  800: '#1e4370',
  900: '#173253',
} as const;

/**
 * Neutros com viés navy (matiz 222°, saturação 10→20%). Cinza puro ao lado do
 * navy parece sujo; o viés faz a UI inteira pertencer à mesma família.
 */
export const gray = {
  50: '#f8f8f9',
  100: '#eeeff1',
  200: '#dddfe3',
  300: '#c4c7cf',
  400: '#999fad',
  500: '#70798f',
  600: '#5b6376', // texto secundário, 6.02:1
  700: '#484f61',
  800: '#353b4b',
  900: '#212631', // texto primário, 15.15:1
} as const;

// ==================== SEMÂNTICAS ====================

/**
 * O verde da marca NÃO é o verde de sucesso (ver justificativa em `green`).
 * Sucesso usa a cauda escura da mesma rampa: mesma família, função distinta.
 */
export const semantic = {
  success: '#3e8226', // 4.74:1 sobre branco
  successHover: '#4a9430',
  successBg: green[100],
  successBorder: green[300],
  successText: green[800], // 6.61:1 para texto pequeno
  successTextOnBg: green[900], // 8.18:1 sobre successBg

  warning: '#d97706', // 3.19:1; só ícone/fundo, nunca texto pequeno
  warningHover: '#b45309', // 5.02:1; este é o warning de TEXTO
  warningBg: '#fbf1e0',
  warningBorder: '#ecc98f',
  warningTextOnBg: '#7a4b08', // 6.69:1

  error: '#d1383d', // 4.83:1; vermelho frio, convive com o navy
  errorHover: '#b92c31',
  errorBg: '#fceaea',
  errorBorder: '#f0b3b5',
  errorTextOnBg: '#a02428',

  info: blue[600], // info É o azul da marca: uma cor a menos no sistema
  infoBg: blue[100],
  infoBorder: blue[300],
  infoTextOnBg: blue[800], // 8.28:1
} as const;

// ==================== PALETA (chaves legadas + novas) ====================

/**
 * Mantém todas as chaves que as 79 páginas consomem, com valores novos.
 * Nenhuma chave foi removida: trocar valor não quebra consumidor; remover, sim.
 */
export const palette = {
  primary: navy[800],
  primaryHover: navy[700],
  primaryActive: navy[900],

  secondary: green[500],
  secondaryHover: green[600], // o verde-marca escurece no hover, não clareia

  accent: green[500], // o lima #A0C42F morreu
  accentHover: green[600],

  success: semantic.success,
  successHover: semantic.successHover,

  warning: semantic.warning,
  warningHover: semantic.warningHover,

  error: semantic.error,
  errorHover: semantic.errorHover,

  textPrimary: gray[900],
  textSecondary: gray[600], // o #8c8c8c anterior reprovava AA
  textDisabled: gray[300],

  bgPrimary: '#ffffff',
  bgSecondary: gray[100],
  bgTertiary: gray[50],

  border: gray[200],
  borderSecondary: gray[100],

  link: blue[600],
  linkHover: blue[700],
  linkActive: blue[800],

  // Sidebar navy, assinatura da marca no produto
  sidebarBg: navy[800],
  sidebarBgActive: navy[900],
  sidebarText: navy[300], // 6.63:1 sobre navy-800
  sidebarTextActive: '#ffffff', // 11.72:1
  sidebarGroupTitle: navy[400],
  sidebarAccent: green[500], // barra do item ativo, 4.77:1 sobre navy-800
} as const;

export const neutral = gray;

export const gradients = {
  /** O roxo #5B3A8F e o lima #A0C42F morreram. Fundo do login. */
  primary: `linear-gradient(135deg, ${navy[800]} 0%, ${navy[900]} 100%)`,
  secondary: `linear-gradient(135deg, ${blue[600]} 0%, ${navy[800]} 100%)`,
  success: `linear-gradient(135deg, ${semantic.success} 0%, ${green[500]} 100%)`,
} as const;

// ==================== ELEVAÇÃO ====================

/**
 * Tinta navy (navy-900), nunca preto puro: sombra preta sobre fundos com viés
 * navy fica marrom-acinzentada e suja a luz fria da marca.
 */
export const shadows = {
  xs: '0 1px 2px rgba(23, 38, 73, 0.06)',
  sm: '0 2px 8px rgba(23, 38, 73, 0.06)',
  md: '0 4px 12px rgba(23, 38, 73, 0.09)',
  lg: '0 8px 24px rgba(23, 38, 73, 0.12)',
  xl: '0 16px 40px rgba(23, 38, 73, 0.16)',
} as const;

export const shadowColors = {
  shadow: 'rgba(23, 38, 73, 0.06)',
  shadowMedium: 'rgba(23, 38, 73, 0.10)',
  shadowStrong: 'rgba(23, 38, 73, 0.16)',
} as const;

/** Máscara de modal/drawer em navy, não preto: mantém a cena na família da marca. */
export const overlay = 'rgba(23, 38, 73, 0.45)';

/** Halo de foco: navy, substitui o azul-AntD padrão. */
export const focusRing = '0 0 0 3px rgba(33, 54, 104, 0.12)';

// ==================== ESPAÇAMENTO / RAIO ====================

/** Base 4. Valores idênticos aos anteriores: já eram múltiplos de 4. */
export const spacing = {
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  xxl: '48px',
} as const;

/**
 * Os bolsões do P/R do wordmark são retângulos de cantos suavemente arredondados
 * e o triângulo é agudo: a marca é geométrica com suavidade contida, não "bubbly".
 * Raios de 16px+ contradiriam o triângulo.
 */
export const borderRadius = {
  sm: '4px',
  md: '6px',
  lg: '8px',
  xl: '12px',
  round: '50%',
  pill: '9999px',
} as const;

// ==================== TIPOGRAFIA ====================

export const typography = {
  fontFamily:
    "'Nunito Sans Variable', 'Nunito Sans', -apple-system, 'Segoe UI', Roboto, sans-serif",
  fontSize: {
    xs: '12px',
    sm: '14px', // corpo do app, densidade de ERP
    md: '16px',
    lg: '18px',
    xl: '20px',
    xxl: '24px',
    xxxl: '32px',
  },
  fontWeight: {
    regular: 400,
    medium: 500, // mantido por compat; novos usos escolhem 600
    semibold: 600,
    bold: 700,
    extrabold: 800, // a Nunito Sans 700 é mais leve que a Omnes Bold
  },
  lineHeight: {
    tight: 1.2,
    normal: 1.5,
    relaxed: 1.75,
  },
} as const;

/**
 * Escala modular (base 14, razão ~1.2 na grade de 4).
 * Regra de uso por contexto: §3.3 do design system.
 */
export const textStyles = {
  display: { fontSize: '32px', lineHeight: '40px', fontWeight: 800, letterSpacing: '-0.5px' },
  h1: { fontSize: '24px', lineHeight: '32px', fontWeight: 800, letterSpacing: '-0.25px' },
  h2: { fontSize: '20px', lineHeight: '28px', fontWeight: 700, letterSpacing: '-0.25px' },
  h3: { fontSize: '16px', lineHeight: '24px', fontWeight: 700, letterSpacing: '0' },
  h4: { fontSize: '14px', lineHeight: '20px', fontWeight: 700, letterSpacing: '0' },
  body: { fontSize: '14px', lineHeight: '22px', fontWeight: 400, letterSpacing: '0' },
  bodySm: { fontSize: '13px', lineHeight: '20px', fontWeight: 400, letterSpacing: '0' },
  caption: { fontSize: '12px', lineHeight: '16px', fontWeight: 400, letterSpacing: '0' },
  overline: { fontSize: '11px', lineHeight: '16px', fontWeight: 700, letterSpacing: '0.08em' },
} as const;

// ==================== MOVIMENTO ====================

/**
 * Entradas usam deslocamento máximo de 12px: movimento contido lê como
 * confiança institucional. Animar só opacity/transform/color/shadow.
 */
export const transitions = {
  fast: '120ms cubic-bezier(0.4, 0, 0.2, 1)',
  normal: '200ms cubic-bezier(0.4, 0, 0.2, 1)',
  slow: '300ms cubic-bezier(0.2, 0, 0, 1)',
} as const;

// ==================== MARCA ====================

export const brand = {
  /** Ângulo da perna do triângulo do "A": arctan(18.56/7.45). */
  chevronAngle: '68deg',
  accentBarWidth: '4px',
  /** Recorte do "A" no Asset 3.svg (ver §6.1 do design system). */
  monogramViewBox: '35.95 -2.11 54.60 54.60',
  tagline: 'CONHECIMENTO QUE SE CONECTA',
} as const;

// ==================== MISC ====================

export const breakpoints = {
  xs: '480px',
  sm: '576px',
  md: '768px',
  lg: '992px',
  xl: '1200px',
  xxl: '1600px',
} as const;

export const zIndex = {
  dropdown: 1000,
  sticky: 1020,
  fixed: 1030,
  modal: 1040,
  popover: 1050,
  tooltip: 1060,
} as const;

/** Sidebar 240px: 200px era apertado para rótulos como "Responsabilidades". */
export const layout = {
  sidebarWidth: 240,
  sidebarWidthCollapsed: 80,
  headerHeight: 64,
} as const;
